import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, RotateCcw, Link, Unlink } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

export const UrlEncoderTool: React.FC = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');

  const encodeUrl = (text: string) => {
    try {
      const encoded = encodeURIComponent(text);
      setOutput(encoded);
      return encoded;
    } catch (err) {
      setOutput('');
      return null;
    }
  };

  const decodeUrl = (encodedText: string) => {
    try {
      const decoded = decodeURIComponent(encodedText);
      setOutput(decoded);
      return decoded;
    } catch (err) {
      setOutput(t('tools.urlEncoder.invalidUrl'));
      return null;
    }
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    
    if (value.trim()) {
      if (mode === 'encode') {
        encodeUrl(value);
      } else {
        decodeUrl(value);
      }
    } else {
      setOutput('');
    }
  };

  const switchMode = () => {
    const newMode = mode === 'encode' ? 'decode' : 'encode';
    setMode(newMode);
    
    // Swap input and output
    const tempInput = input;
    setInput(output);
    setOutput(tempInput);
    
    // Process the swapped input
    if (output.trim()) {
      if (newMode === 'encode') {
        encodeUrl(output);
      } else {
        decodeUrl(output);
      }
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
  };

  const sampleUrls = [
    'https://example.com/search?q=hello world&lang=en',
    'https://example.com/path with spaces/file.html',
    'mailto:user@example.com?subject=Hello World&body=This is a test',
    'https://example.com/api?data={"name":"John","age":30}',
  ];

  const loadSample = (url: string) => {
    setInput(url);
    if (mode === 'encode') {
      encodeUrl(url);
    } else {
      decodeUrl(url);
    }
  };

  return (
    <div className="space-y-6">
      {/* Mode Selector */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.urlEncoder.name')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={() => {
                setMode('encode');
                if (input) encodeUrl(input);
              }}
              variant={mode === 'encode' ? 'primary' : 'secondary'}
            >
              <Link className="w-4 h-4 mr-2" />
              {t('tools.urlEncoder.encode')}
            </Button>
            <Button
              onClick={() => {
                setMode('decode');
                if (input) decodeUrl(input);
              }}
              variant={mode === 'decode' ? 'primary' : 'secondary'}
            >
              <Unlink className="w-4 h-4 mr-2" />
              {t('tools.urlEncoder.decode')}
            </Button>
            <Button
              onClick={switchMode}
              variant="outline"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {t('tools.urlEncoder.switch')}
            </Button>
            <Button
              onClick={() => output && copyToClipboard(output)}
              disabled={!output}
              variant="outline"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('common.copy')}
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
            >
              {t('tools.urlEncoder.clear')}
            </Button>
          </div>

          {/* Sample URLs */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tools.urlEncoder.sampleUrls')}:
            </label>
            <div className="space-y-2">
              {sampleUrls.map((url, index) => (
                <button
                  key={index}
                  onClick={() => loadSample(url)}
                  className="block w-full text-left p-2 text-sm bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 rounded border font-mono text-gray-600 dark:text-gray-400 truncate"
                >
                  {url}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Input/Output */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>
              {mode === 'encode' ? t('tools.urlEncoder.originalUrl') : t('tools.urlEncoder.urlEncodedString')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={
                mode === 'encode'
                  ? t('tools.urlEncoder.enterUrlToEncode')
                  : t('tools.urlEncoder.enterUrlToDecode')
              }
              className="min-h-[300px] font-mono text-sm"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                {mode === 'encode' ? t('tools.urlEncoder.urlEncodedResult') : t('tools.urlEncoder.decodedUrl')}
              </span>
              {output && (
                <span className="text-sm text-gray-500">
                  {output.length} {t('tools.urlEncoder.chars')}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder={
                mode === 'encode'
                  ? t('tools.urlEncoder.encodedResultPlaceholder')
                  : t('tools.urlEncoder.decodedResultPlaceholder')
              }
              className="min-h-[300px] font-mono text-sm bg-gray-50 dark:bg-gray-800"
            />
          </CardContent>
        </Card>
      </div>

      {/* URL Components Analysis */}
      {output && mode === 'decode' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.urlEncoder.urlComponents')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(() => {
                try {
                  const url = new URL(output);
                  return (
                    <div className="grid gap-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Protocol:</span>
                          <div className="font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded">
                            {url.protocol}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Host:</span>
                          <div className="font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded">
                            {url.host}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Pathname:</span>
                          <div className="font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded">
                            {url.pathname}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Search:</span>
                          <div className="font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded">
                            {url.search || '(none)'}
                          </div>
                        </div>
                      </div>
                      {url.searchParams.size > 0 && (
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Query Parameters:</span>
                          <div className="mt-2 space-y-1">
                            {Array.from(url.searchParams.entries()).map(([key, value], index) => (
                              <div key={index} className="flex items-center space-x-2 text-sm">
                                <span className="font-mono bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                                  {key}
                                </span>
                                <span>=</span>
                                <span className="font-mono bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded">
                                  {value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                } catch (err) {
                  return (
                    <div className="text-sm text-gray-500">
                      Not a valid URL format
                    </div>
                  );
                }
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statistics */}
      {output && (
        <Card>
          <CardHeader>
            <CardTitle>Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Input Length:</span>
                <div className="font-mono">{input.length.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Output Length:</span>
                <div className="font-mono">{output.length.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Size Change:</span>
                <div className="font-mono">
                  {input.length > 0 
                    ? `${((output.length - input.length) / input.length * 100).toFixed(1)}%`
                    : '0%'
                  }
                </div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Special Chars:</span>
                <div className="font-mono">
                  {mode === 'encode' 
                    ? (input.match(/[^a-zA-Z0-9\-_.~]/g) || []).length
                    : (output.match(/%[0-9A-Fa-f]{2}/g) || []).length
                  }
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
